<script setup lang="ts">
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { computed, inject, ref } from 'vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import Tag from '@/Components/Utils/Tag.vue'
import Icon from '@/Components/Utils/Icon.vue'
import CopyableTextarea from '@/Components/Utils/CopyableTextarea.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import AddOrderDesignModal from './AddOrderDesignModal.vue'
import BrandExclusiveTag from '@/Pages/Kasir/Brands/BrandExclusiveTag.vue'
import PureMultiselectInfiniteScroll from '@/Components/Pure/PureMultiselectInfiniteScroll.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBoxes, faExternalLink, faFilePdf, faImage, faIndustry, faPencil, faPrint, faShippingFast, faSignature, faTrashAlt, faUpload } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import { DatePicker, Dialog, FileUpload, InputNumber, InputText, Select } from 'primevue'

library.add(faBoxes, faExternalLink, faFilePdf, faImage, faIndustry, faPencil, faPrint, faShippingFast, faSignature, faTrashAlt, faUpload)
import { useCopyText } from '@/Composables/useCopyText'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    data: any
    paymentTypes: { value: string, label: string }[]
    couriers: { id: number, name: string, requires_tracking_number: boolean }[]
    confirmationText: string
    invoiceMessage: string
    routes: Record<string, { name: string, parameters?: any[] }>
}>()

const layout = inject('layout', layoutStructure)
const isSuperAdmin = computed(() => layout?.auth?.role === 'super_admin')

const isEditingReference = ref(false)
const referenceForm = useForm({
    reference: props.data.reference as string,
})

const cancelEditReference = () => {
    referenceForm.reference = props.data.reference
    referenceForm.clearErrors()
    isEditingReference.value = false
}

const saveReference = () => {
    referenceForm.patch(route(props.routes.update_reference.name, props.routes.update_reference.parameters), {
        preserveScroll: true,
        onSuccess: () => {
            referenceForm.defaults('reference', props.data.reference)
            isEditingReference.value = false
        },
    })
}

const paymentForm = useForm({
    type: props.paymentTypes[0]?.value,
    amount: null as number | null,
    paid_at: new Date().toISOString().slice(0, 10),
    note: '',
    proof_image: null as File | null,
})

const proofInputKey = ref(0)

const uploadingProofPaymentId = ref<number | null>(null)
const proofErrors = ref<Record<number, string>>({})

const toDate = (value: string): Date | null => value ? new Date(`${value}T00:00:00`) : null
const toDateString = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const uploadProof = (paymentId: number, image?: File) => {
    if (!image) return

    uploadingProofPaymentId.value = paymentId
    delete proofErrors.value[paymentId]

    router.post(route(props.routes.payment_proof.name, [paymentId]), { proof_image: image }, {
        preserveScroll: true,
        onError: (errors: Record<string, string>) => proofErrors.value[paymentId] = errors.proof_image,
        onFinish: () => uploadingProofPaymentId.value = null,
    })
}

interface QuantityShortcut {
    label: string
    summaryLabel: string
    quantity: number
}

const editingQuantityProductId = ref<number | null>(null)
const quantityForm = useForm({
    quantity: null as number | null,
})

const quantityShortcuts = (product: any): QuantityShortcut[] => {
    const manufacture = product.manufacture
    if (!manufacture) return []

    return [
        { label: 'Sesuai hasil finishing', summaryLabel: 'Finishing', quantity: manufacture.finishing_count },
        { label: 'Sesuai hasil validasi', summaryLabel: 'Validasi', quantity: manufacture.validated_count },
        { label: 'Sesuai validasi customer', summaryLabel: 'Validasi customer', quantity: manufacture.customer_validated_count },
    ].filter(shortcut => shortcut.quantity !== null && shortcut.quantity !== undefined)
}

const pendingQuantityChange = ref<{ product: any, shortcut: QuantityShortcut } | null>(null)
const isApplyingQuantityChange = ref(false)

const askQuantityChange = (product: any, shortcut: QuantityShortcut) => {
    pendingQuantityChange.value = { product, shortcut }
}

const applyQuantityChange = () => {
    const pending = pendingQuantityChange.value
    if (!pending) return

    isApplyingQuantityChange.value = true
    router.patch(route(props.routes.product_quantity.name, [pending.product.id]), { quantity: pending.shortcut.quantity }, {
        preserveScroll: true,
        onSuccess: () => pendingQuantityChange.value = null,
        onFinish: () => isApplyingQuantityChange.value = false,
    })
}

const startEditQuantity = (product: { id: number, quantity: number }) => {
    editingQuantityProductId.value = product.id
    quantityForm.quantity = product.quantity
    quantityForm.clearErrors()
}

const cancelEditQuantity = () => {
    editingQuantityProductId.value = null
    quantityForm.clearErrors()
}

const saveEditingQuantity = () => {
    if (editingQuantityProductId.value === null) return

    quantityForm.clearErrors()
    quantityForm.patch(route(props.routes.product_quantity.name, [editingQuantityProductId.value]), {
        preserveScroll: true,
        onSuccess: () => editingQuantityProductId.value = null,
    })
}

const shipmentForm = useForm({
    cs_courier_id: (props.couriers[0]?.id ?? null) as number | null,
    tracking_number: '',
})

const selectedCourier = computed(() => props.couriers.find(courier => courier.id === shipmentForm.cs_courier_id))

const addShipment = () => {
    shipmentForm.post(route(props.routes.store_shipment.name, props.routes.store_shipment.parameters), {
        preserveScroll: true,
        onSuccess: () => shipmentForm.reset('tracking_number'),
    })
}

const isAdjustmentPayment = computed(() => paymentForm.type === 'penyesuaian')

const overpaidAmount = computed(() => Math.max(0, -props.data.remaining_amount))

const addPayment = () => {
    paymentForm.post(route(props.routes.store_payment.name, props.routes.store_payment.parameters), {
        preserveScroll: true,
        onSuccess: () => {
            paymentForm.reset('amount', 'note', 'proof_image')
            proofInputKey.value++
        },
    })
}

const designForm = useForm({
    design_ids: props.data.designs.map((design: any) => design.id) as number[],
})

const attachedDesignIds = (): number[] => props.data.designs.map((design: any) => design.id)

const designsToAttach = ref<number[]>([])
const designPickerKey = ref(0)

const syncDesigns = (designIds: number[], onDone?: () => void, onFinish?: () => void) => {
    designForm.design_ids = designIds

    designForm.patch(route(props.routes.sync_designs.name, props.routes.sync_designs.parameters), {
        preserveScroll: true,
        onSuccess     : onDone,
        onFinish      : onFinish,
    })
}

const attachDesigns = () => syncDesigns([...attachedDesignIds(), ...designsToAttach.value], () => {
    designsToAttach.value = []
    designPickerKey.value++
})

const detachingDesignId = ref<number | null>(null)

const detachDesign = (designId: number) => {
    detachingDesignId.value = designId

    syncDesigns(
        attachedDesignIds().filter(id => id !== designId),
        () => designPickerKey.value++,
        () => detachingDesignId.value = null,
    )
}

const isExclusiveBrand = computed(() => props.data.brand.status === 'eksklusif')

const creatingManufactureProductId = ref<number | null>(null)
const manufactureErrors = ref<Record<number, string>>({})

const createManufacture = (productId: number) => {
    creatingManufactureProductId.value = productId
    delete manufactureErrors.value[productId]

    router.post(route(props.routes.product_manufacture.name, [productId]), {}, {
        preserveScroll: true,
        onError: (errors: Record<string, string>) => manufactureErrors.value[productId] = errors.manufacture,
        onFinish: () => creatingManufactureProductId.value = null,
    })
}

const isAddDesignOpen = ref(false)

const onDesignCreated = () => designPickerKey.value++

const printTypeCounts = computed(() => ({
    ditakurnia: props.data.products.filter((product: any) => product.print_type === 'ditakurnia').length,
    luar      : props.data.products.filter((product: any) => product.print_type !== 'ditakurnia').length,
}))

const paidRatio = computed(() => {
    if (!props.data.total_amount) return 0
    return Math.max(0, Math.min(100, Math.round((props.data.paid_amount / props.data.total_amount) * 100)))
})
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #otherBefore>
            <a :href="route(routes.download_pdf.name, routes.download_pdf.parameters)" download>
                <Button style="tertiary" label="Download PDF" icon="fal fa-file-pdf" />
            </a>
            <a :href="route(routes.shipping_label.name, routes.shipping_label.parameters)" target="_blank" rel="noopener">
                <Button v-tooltip="'Cetak 3 resi pengiriman di kertas A4'" style="tertiary" label="Cetak Resi" icon="fal fa-print" />
            </a>
            <a :href="route(routes.shipping_label.name, routes.shipping_label.parameters) + '?paper=f4'" target="_blank" rel="noopener">
                <Button v-tooltip="'Cetak 4 resi pengiriman di kertas F4'" style="tertiary" label="Cetak Resi F4" icon="fal fa-print" />
            </a>
        </template>
    </PageHeading>

    <div class="grid gap-4 px-4 py-4 lg:grid-cols-3">
        <div class="flex flex-col gap-4 lg:col-span-2">
            <section class="overflow-hidden rounded-xl border border-sky-400">
                <div class="flex items-center justify-between border-b border-gray-900/5 bg-gray-50 px-6 py-4">
                    <div>
                        <div v-if="isEditingReference" class="flex flex-wrap items-center gap-2">
                            <InputText
                                v-model="referenceForm.reference"
                                size="small"
                                class="font-mono"
                                :class="referenceForm.errors.reference ? 'errorShake' : ''"
                                :invalid="!!referenceForm.errors.reference"
                                autofocus
                                @input="referenceForm.clearErrors('reference')"
                                @keydown.enter.prevent="saveReference"
                                @keydown.esc="cancelEditReference"
                            />
                            <Button size="xs" type="save" label="Simpan" :loading="referenceForm.processing" @click="saveReference" />
                            <Button size="xs" type="tertiary" label="Batal" @click="cancelEditReference" />
                            <div v-if="referenceForm.errors.reference" class="w-full text-xs text-red-500">{{ referenceForm.errors.reference }}</div>
                        </div>
                        <div v-else class="flex items-center gap-x-2 font-mono text-sm text-gray-500">
                            {{ data.reference }}
                            <button
                                v-if="isSuperAdmin"
                                type="button"
                                class="text-gray-400 hover:text-gray-600"
                                v-tooltip="'Ubah nama pesanan'"
                                @click="isEditingReference = true"
                            >
                                <FontAwesomeIcon icon="fal fa-pencil" fixed-width aria-hidden="true" />
                            </button>
                        </div>
                        <div class="flex items-center gap-x-2">
                            <Link :href="route('kasir.brands.show', data.brand.slug)" class="text-lg font-bold primaryLink">
                                {{ data.brand.name }}
                            </Link>
                            <BrandExclusiveTag v-if="data.brand.status === 'eksklusif'" />
                        </div>
                        <div class="text-sm text-gray-500">{{ data.brand.customer_name }}</div>
                    </div>
                    <div class="flex items-center gap-x-2">
                        <Icon v-if="data.state_icon" :data="data.state_icon" />
                        <span class="font-medium">{{ data.state_label }}</span>
                    </div>
                </div>

                <table class="w-full text-sm">
                    <thead class="text-xs uppercase text-gray-400">
                        <tr>
                            <th class="px-6 py-2 text-left">Produk</th>
                            <th class="px-6 py-2 text-right">Qty</th>
                            <th class="px-6 py-2 text-right">Harga</th>
                            <th class="px-6 py-2 text-right">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="product in data.products" :key="product.id">
                            <td class="px-6 py-3">
                                <div class="font-medium">{{ product.name }}</div>
                                <div class="text-xs text-gray-400">
                                    {{ product.category_name }}
                                    <span v-if="product.print_type_label"> &middot; {{ product.print_type_label }}</span>
                                </div>
                                <div v-if="product.manufacture_name && !product.stock" class="text-xs text-cyan-600">
                                    Manufacture: {{ product.manufacture_name }}
                                </div>
                                <Link
                                    v-if="product.stock"
                                    :href="route('kasir.stocks.show', product.stock.id)"
                                    class="mt-1 inline-flex items-center gap-x-1.5 text-xs secondaryLink"
                                >
                                    <FontAwesomeIcon icon="fal fa-boxes" fixed-width aria-hidden="true" />
                                    Dari {{ product.stock.is_general ? 'stok umum' : 'stok brand' }}
                                    &middot;
                                    {{ data.stock_deducted_at ? 'stok sudah dikurangi (terkirim)' : data.canceled_at ? 'reservasi dilepas' : 'reserved, dikurangi saat dikirim' }}
                                </Link>
                                <Link
                                    v-if="product.manufacture"
                                    :href="route('manufacture.show', product.manufacture.slug)"
                                    class="mt-1 inline-flex items-center gap-x-1.5 text-xs secondaryLink"
                                >
                                    <Icon :data="product.manufacture.state_icon" />
                                    {{ product.manufacture.reference }} &middot; {{ product.manufacture.state_label }}
                                </Link>
                                <div v-else-if="product.needs_manufacture && isExclusiveBrand && !data.canceled_at" class="mt-1">
                                    <Button
                                        size="xxs"
                                        type="indigo"
                                        icon="fal fa-industry"
                                        label="Buat produksi"
                                        v-tooltip="'Buat produksi untuk pesanan ini (tidak perlu DP Produksi)'"
                                        :loading="creatingManufactureProductId === product.id"
                                        :disabled="creatingManufactureProductId !== null"
                                        @click="createManufacture(product.id)"
                                    />
                                    <div v-if="manufactureErrors[product.id]" class="mt-1 text-xs text-red-500">{{ manufactureErrors[product.id] }}</div>
                                </div>
                                <div v-else-if="product.needs_manufacture" class="mt-1 text-xs text-amber-600">
                                    Produksi dibuat setelah DP Produksi dibayar
                                </div>
                                <div
                                    v-if="product.manufacture"
                                    class="mt-1 flex flex-wrap gap-x-2 text-xs text-gray-500"
                                >
                                    <template v-for="(shortcut, shortcutIndex) in quantityShortcuts(product)" :key="shortcut.label">
                                        <span v-if="shortcutIndex > 0">&middot;</span>
                                        <button
                                            type="button"
                                            class="hover:text-cyan-600 disabled:cursor-default disabled:no-underline disabled:hover:text-gray-500 border border-transparent hover:border-cyan-500 hover:disabled:border-gray-300 px-1 rounded-full"
                                            :disabled="!!data.canceled_at || shortcut.quantity === product.quantity"
                                            v-tooltip="shortcut.quantity === product.quantity ? 'Sudah sesuai jumlah pesanan' : `Jadikan jumlah pesanan ${useFormatNumber(shortcut.quantity)} pcs`"
                                            @click="askQuantityChange(product, shortcut)"
                                        >
                                            {{ shortcut.summaryLabel }}: {{ useFormatNumber(shortcut.quantity) }} pcs
                                        </button>
                                    </template>
                                </div>
                            </td>
                            <td class="px-6 py-3 text-right align-top">
                                <div v-if="editingQuantityProductId === product.id" class="ml-auto flex w-40 flex-col items-end gap-y-1">
                                    <InputNumber
                                        v-model="quantityForm.quantity"
                                        :min="0"
                                        locale="id-ID"
                                        size="small"
                                        fluid
                                        autofocus
                                        :class="quantityForm.errors.quantity ? 'errorShake' : ''"
                                        :invalid="!!quantityForm.errors.quantity"
                                        @keydown.enter.prevent="saveEditingQuantity"
                                        @keydown.esc="cancelEditQuantity"
                                    />
                                    <button
                                        v-for="shortcut in quantityShortcuts(product)"
                                        :key="shortcut.label"
                                        type="button"
                                        class="text-xs text-cyan-600 underline disabled:cursor-default disabled:opacity-40"
                                        :disabled="quantityForm.quantity === shortcut.quantity"
                                        @click="quantityForm.quantity = shortcut.quantity; quantityForm.clearErrors('quantity')"
                                    >
                                        {{ shortcut.label }} ({{ useFormatNumber(shortcut.quantity) }})
                                    </button>
                                    <div v-if="quantityForm.errors.quantity" class="text-xs text-red-500">{{ quantityForm.errors.quantity }}</div>
                                    <div class="mt-1 flex gap-x-1">
                                        <Button size="xxs" type="tertiary" label="Batal" @click="cancelEditQuantity" />
                                        <Button size="xxs" type="save" label="Simpan" :loading="quantityForm.processing" @click="saveEditingQuantity" />
                                    </div>
                                </div>
                                <div v-else class="flex items-center justify-end gap-x-1.5">
                                    {{ useFormatNumber(product.quantity) }}
                                    <button
                                        v-if="!data.canceled_at"
                                        type="button"
                                        class="text-gray-400 hover:text-gray-600"
                                        v-tooltip="'Ubah jumlah'"
                                        @click="startEditQuantity(product)"
                                    >
                                        <FontAwesomeIcon icon="fal fa-pencil" fixed-width aria-hidden="true" />
                                    </button>
                                </div>
                            </td>
                            <td class="px-6 py-3 text-right">{{ useFormatCurrency(product.price) }}</td>
                            <td class="px-6 py-3 text-right font-medium">{{ useFormatCurrency(product.total_amount) }}</td>
                        </tr>
                    </tbody>
                    <tfoot class="border-t border-gray-200 text-sm">
                        <tr>
                            <td colspan="3" class="px-6 py-2 text-right text-gray-500">Total produk</td>
                            <td class="px-6 py-2 text-right">{{ useFormatCurrency(data.products_amount) }}</td>
                        </tr>
                        <tr>
                            <td colspan="3" class="px-6 py-2 text-right text-gray-500">Ongkir</td>
                            <td class="px-6 py-2 text-right">{{ useFormatCurrency(data.shipping_cost) }}</td>
                        </tr>
                        <tr :class="data.shipping_subsidy ? '' : 'opacity-40'">
                            <td colspan="3" class="px-6 py-2 text-right text-gray-500">Subsidi ongkir</td>
                            <td class="px-6 py-2 text-right" :class="data.shipping_subsidy ? 'text-emerald-600' : 'text-gray-500'">
                                -{{ useFormatCurrency(data.shipping_subsidy ?? 0) }}
                            </td>
                        </tr>
                        <tr v-if="data.additional_charge">
                            <td colspan="3" class="px-6 py-2 text-right text-gray-500">
                                Biaya tambahan
                                <span v-if="data.additional_charge_note" class="text-xs">({{ data.additional_charge_note }})</span>
                            </td>
                            <td class="px-6 py-2 text-right">{{ useFormatCurrency(data.additional_charge) }}</td>
                        </tr>
                        <tr class="bg-gray-50">
                            <td colspan="3" class="px-6 py-3 text-right font-semibold">Total tagihan</td>
                            <td class="px-6 py-3 text-right text-base font-bold">{{ useFormatCurrency(data.total_amount) }}</td>
                        </tr>
                    </tfoot>
                </table>

                <div v-if="data.note" class="border-t border-gray-100 px-6 py-3 text-sm">
                    <span class="text-gray-500">Catatan:</span> {{ data.note }}
                </div>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 font-semibold text-gray-700">
                    Pembayaran
                </div>

                <div class="px-6 py-4">
                    <div class="mb-1 flex justify-between text-sm">
                        <span class="text-gray-500">Terbayar {{ useFormatCurrency(data.paid_amount) }}</span>
                        <span v-if="data.canceled_at" class="text-gray-500">
                            Dibatalkan{{ data.paid_amount > 0 ? ' · perlu refund' : '' }}
                        </span>
                        <span v-else-if="overpaidAmount > 0" class="font-medium text-amber-600">
                            Kelebihan bayar {{ useFormatCurrency(overpaidAmount) }}
                        </span>
                        <span v-else :class="data.remaining_amount > 0 ? 'text-red-500' : 'text-emerald-600'">
                            Sisa {{ useFormatCurrency(data.remaining_amount) }}
                        </span>
                    </div>
                    <div class="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                        <div
                            class="h-full rounded-full"
                            :class="data.canceled_at ? 'bg-gray-300' : 'bg-emerald-500'"
                            :style="{ width: `${paidRatio}%` }"
                        />
                    </div>
                    <div v-if="data.canceled_at && data.paid_amount > 0" class="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                        Pesanan dibatalkan, tapi {{ useFormatCurrency(data.paid_amount) }} sudah diterima. Pengembalian dana diurus di luar aplikasi.
                    </div>
                    <div v-else-if="overpaidAmount > 0" class="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                        Pembayaran melebihi total tagihan sebesar <span class="font-semibold">{{ useFormatCurrency(overpaidAmount) }}</span>.
                        Jika kelebihan dikembalikan ke customer, catat pembayaran <span class="font-semibold">Penyesuaian</span>
                        dengan nominal <span class="font-semibold">-{{ useFormatCurrency(overpaidAmount) }}</span>.
                    </div>
                </div>

                <ul v-if="data.payments.length" class="divide-y divide-gray-100 text-sm">
                    <li v-for="payment in data.payments" :key="payment.id" class="flex items-center justify-between px-6 py-3">
                        <div>
                            <div class="font-medium">{{ payment.type_label }}</div>
                            <div class="text-xs text-gray-400">
                                {{ useFormatTime(payment.paid_at) }}
                                <span v-if="payment.note"> &middot; {{ payment.note }}</span>
                            </div>
                            <a
                                v-if="payment.proof_image_url"
                                :href="payment.proof_image_url"
                                target="_blank"
                                rel="noopener"
                                class="mt-1 inline-flex items-center gap-x-1 text-xs text-blue-500 underline"
                            >
                                <FontAwesomeIcon icon="fal fa-image" fixed-width aria-hidden="true" />
                                Lihat bukti transfer
                            </a>
                            <div v-else-if="!data.canceled_at" class="mt-1 flex items-center gap-x-2">
                                <FileUpload
                                    mode="basic"
                                    accept="image/jpeg,image/png,image/webp"
                                    customUpload
                                    chooseLabel="Upload bukti transfer"
                                    severity="secondary"
                                    class="p-button-sm p-button-outlined"
                                    :disabled="uploadingProofPaymentId === payment.id"
                                    @select="event => uploadProof(payment.id, event.files[0])"
                                />
                                <LoadingIcon v-if="uploadingProofPaymentId === payment.id" />
                            </div>
                            <div v-if="proofErrors[payment.id]" class="mt-1 text-xs text-red-500">{{ proofErrors[payment.id] }}</div>
                        </div>
                        <div class="flex items-center gap-x-4">
                            <span class="font-medium" :class="payment.amount < 0 ? 'text-red-500' : ''">{{ useFormatCurrency(payment.amount) }}</span>
                            <ModalConfirmationDelete
                                v-if="!data.canceled_at"
                                title="Hapus pembayaran ini?"
                                :description="payment.type === 'dp_produksi'
                                    ? 'Pembayaran akan dihapus dan total terbayar dihitung ulang. Jika ini satu-satunya DP Produksi, semua produksi pesanan ini yang belum dikerjakan akan DIHAPUS. Produksi akan muncul lagi saat DP Produksi dicatat kembali.'
                                    : 'Pembayaran akan dihapus dan total terbayar dihitung ulang.'"
                                :routeDelete="{ name: routes.delete_payment.name, parameters: [payment.id], method: 'delete' }"
                                preserveScroll
                            >
                                <template #default="{ changeModel, isLoadingdelete }">
                                    <button class="text-gray-400 hover:text-red-500" @click="() => changeModel()">
                                        <LoadingIcon v-if="isLoadingdelete" />
                                        <FontAwesomeIcon v-else icon="fal fa-times" fixed-width aria-hidden="true" />
                                    </button>
                                </template>
                            </ModalConfirmationDelete>
                        </div>
                    </li>
                </ul>
                <div v-else class="px-6 py-4 text-center text-sm text-gray-400">Belum ada pembayaran</div>

                <div v-if="data.canceled_at" class="border-t border-gray-100 bg-gray-50 px-6 py-4 text-center text-sm text-gray-400">
                    Pesanan sudah dibatalkan, pembayaran baru tidak bisa dicatat.
                </div>
                <div v-else class="border-t border-gray-100 bg-gray-50 px-6 py-4">
                    <div class="grid gap-3 md:grid-cols-6">
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Jenis</label>
                            <Select
                                v-model="paymentForm.type"
                                :options="paymentTypes"
                                optionLabel="label"
                                optionValue="value"
                                fluid
                            />
                        </div>
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Nominal</label>
                            <InputNumber
                                :modelValue="paymentForm.amount"
                                mode="currency"
                                currency="IDR"
                                locale="id-ID"
                                :maxFractionDigits="0"
                                :min="isAdjustmentPayment ? undefined : 0"
                                fluid
                                @update:modelValue="value => paymentForm.amount = value ?? null"
                            />
                        </div>
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Tanggal</label>
                            <DatePicker
                                :modelValue="toDate(paymentForm.paid_at)"
                                dateFormat="dd/mm/yy"
                                showIcon
                                iconDisplay="input"
                                fluid
                                @update:modelValue="value => paymentForm.paid_at = value instanceof Date ? toDateString(value) : ''"
                            />
                        </div>
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Catatan</label>
                            <InputText v-model="paymentForm.note" placeholder="Masukkan catatan jika ada" fluid />
                        </div>
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Bukti transfer</label>
                            <FileUpload
                                :key="proofInputKey"
                                mode="basic"
                                accept="image/jpeg,image/png,image/webp"
                                customUpload
                                chooseLabel="Pilih gambar"
                                severity="secondary"
                                class="p-button-outlined w-full"
                                @select="event => { paymentForm.proof_image = event.files[0] ?? null; paymentForm.clearErrors('proof_image') }"
                            />
                        </div>
                        <div class="flex items-end">
                            <Button full type="secondary" label="Catat" icon="fal fa-money-bill-wave" :loading="paymentForm.processing" @click="addPayment" />
                        </div>
                    </div>
                    <div v-if="paymentForm.errors.amount" class="mt-2 text-xs text-red-500">{{ paymentForm.errors.amount }}</div>
                    <div v-if="paymentForm.errors.proof_image" class="mt-2 text-xs text-red-500">{{ paymentForm.errors.proof_image }}</div>
                    <p class="mt-2 text-xs text-gray-400">
                        DP Desain &rarr; antri desain, DP Produksi &rarr; antri cetak, Pelunasan &rarr; pengiriman, Ongkir &rarr; selesai.
                        Penyesuaian &rarr; status tidak berubah, nominal boleh minus.
                    </p>
                </div>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="flex items-center justify-between border-b border-gray-900/5 bg-gray-50 px-6 py-3">
                    <span class="font-semibold text-gray-700">Pengiriman</span>
                    <Link :href="route('kasir.settings.couriers.index')" class="text-xs text-blue-500 underline">
                        Atur list pengiriman
                    </Link>
                </div>

                <table v-if="data.shipments.length" class="w-full text-sm">
                    <thead class="text-xs uppercase text-gray-400">
                        <tr>
                            <th class="px-6 py-2 text-left">Ekspedisi</th>
                            <th class="px-6 py-2 text-left">No. resi</th>
                            <th class="px-6 py-2 text-left">Dicatat</th>
                            <th class="px-6 py-2" />
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr v-for="shipment in data.shipments" :key="shipment.id">
                            <td class="px-6 py-3 font-medium">{{ shipment.courier_name }}</td>
                            <td class="px-6 py-3">
                                <div class="flex flex-wrap items-center gap-x-3">
                                    <span class="font-mono">{{ shipment.tracking_number || '-' }}</span>
                                    <a
                                        v-if="shipment.tracking_url"
                                        :href="shipment.tracking_url"
                                        target="_blank"
                                        rel="noopener"
                                        class="inline-flex items-center gap-x-1 text-xs text-blue-500 underline"
                                        v-tooltip="shipment.tracking_number ? 'No. resi otomatis disalin, tinggal tempel di halaman ekspedisi' : undefined"
                                        @click="shipment.tracking_number && useCopyText(shipment.tracking_number)"
                                    >
                                        Lacak
                                        <FontAwesomeIcon icon="fal fa-external-link" fixed-width aria-hidden="true" />
                                    </a>
                                </div>
                            </td>
                            <td class="px-6 py-3 text-gray-500">{{ useFormatTime(shipment.created_at) }}</td>
                            <td class="px-6 py-3 text-right">
                                <ModalConfirmationDelete
                                    title="Hapus data pengiriman ini?"
                                    :description="`${shipment.courier_name}${shipment.tracking_number ? ` (${shipment.tracking_number})` : ''} akan dihapus dari pesanan.`"
                                    :routeDelete="{ name: routes.delete_shipment.name, parameters: [shipment.id], method: 'delete' }"
                                    preserveScroll
                                >
                                    <template #default="{ changeModel, isLoadingdelete }">
                                        <button class="text-gray-400 hover:text-red-500" @click="() => changeModel()">
                                            <LoadingIcon v-if="isLoadingdelete" />
                                            <FontAwesomeIcon v-else icon="fal fa-times" fixed-width aria-hidden="true" />
                                        </button>
                                    </template>
                                </ModalConfirmationDelete>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-else class="px-6 py-4 text-center text-sm text-gray-400">Belum ada data pengiriman</div>

                <div class="border-t border-gray-100 bg-gray-50 px-6 py-4">
                    <div class="grid gap-3 md:grid-cols-4">
                        <div>
                            <label class="mb-1 block text-xs text-gray-500">Ekspedisi</label>
                            <Select
                                v-model="shipmentForm.cs_courier_id"
                                :options="couriers"
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Pilih ekspedisi"
                                emptyMessage="Belum ada ekspedisi aktif"
                                :invalid="!!shipmentForm.errors.cs_courier_id"
                                fluid
                                @change="shipmentForm.clearErrors()"
                            />
                        </div>
                        <div class="md:col-span-2">
                            <label class="mb-1 block text-xs text-gray-500">
                                No. resi
                                <span v-if="!selectedCourier?.requires_tracking_number" class="text-gray-400">(opsional)</span>
                            </label>
                            <InputText
                                v-model="shipmentForm.tracking_number"
                                placeholder="Masukkan nomor resi"
                                class="font-mono placeholder:font-sans"
                                :class="shipmentForm.errors.tracking_number ? 'errorShake' : ''"
                                :invalid="!!shipmentForm.errors.tracking_number"
                                fluid
                                @input="shipmentForm.clearErrors('tracking_number')"
                                @keydown.enter.prevent="addShipment"
                            />
                        </div>
                        <div class="flex items-end">
                            <Button full type="secondary" label="Simpan" icon="fal fa-shipping-fast" :loading="shipmentForm.processing" @click="addShipment" />
                        </div>
                    </div>
                    <div v-if="shipmentForm.errors.cs_courier_id" class="mt-2 text-xs text-red-500">{{ shipmentForm.errors.cs_courier_id }}</div>
                    <div v-if="shipmentForm.errors.tracking_number" class="mt-2 text-xs text-red-500">{{ shipmentForm.errors.tracking_number }}</div>
                </div>
            </section>

            <section class="xoverflow-hidden rounded-xl border border-gray-200">
                <div class="flex items-center justify-between border-b border-gray-900/5 bg-gray-50 px-6 py-3">
                    <span class="font-semibold text-gray-700">
                        Desain
                        <span v-if="data.design_type_label" class="ml-1 text-sm font-normal text-gray-500">({{ data.design_type_label }})</span>
                    </span>
                    <Button size="xs" type="tertiary" icon="fal fa-plus" label="Tambah desain" @click="isAddDesignOpen = true" />
                </div>
                <ul v-if="data.designs.length" class="divide-y divide-gray-100 text-sm">
                    <li v-for="design in data.designs" :key="design.id" class="flex items-start justify-between gap-x-3 px-6 py-3">
                        <div class="min-w-0">
                            <div class="font-medium">{{ design.name }}</div>
                            <div class="truncate text-xs text-gray-400">{{ design.product || 'Semua produk' }}</div>
                            <div class="mt-0.5 text-xs text-gray-400">
                                Dibuat {{ useFormatTime(design.created_at, { formatTime: 'hm' }) }}
                            </div>
                            <div v-if="design.note" class="mt-1 rounded-md bg-gray-50 p-2 text-xs text-gray-600">
                                {{ design.note }}
                            </div>
                        </div>
                        <div class="flex shrink-0 items-center gap-x-3">
                            <a :href="design.link" target="_blank" rel="noopener" class="secondaryLink">
                                <FontAwesomeIcon icon="fal fa-external-link" fixed-width aria-hidden="true" />
                            </a>
                            <button
                                v-tooltip="'Lepas dari pesanan'"
                                class="text-gray-400 hover:text-red-500 disabled:hover:text-gray-400"
                                :disabled="designForm.processing"
                                @click="detachDesign(design.id)"
                            >
                                <LoadingIcon v-if="detachingDesignId === design.id" />
                                <FontAwesomeIcon v-else icon="fal fa-times" fixed-width aria-hidden="true" />
                            </button>
                        </div>
                    </li>
                </ul>
                <div v-else class="px-6 py-4 text-center text-sm text-gray-400">
                    Belum ada desain yang dilampirkan ke pesanan ini.
                </div>

                <div class="border-t border-gray-100 px-6 py-4">
                    <div class="mb-2 text-xs text-gray-500">Lampirkan desain lain dari brand ini</div>

                    <PureMultiselectInfiniteScroll
                        :key="designPickerKey"
                        v-model="designsToAttach"
                        mode="tags"
                        :fetchRoute="routes.design_options"
                        labelProp="name"
                        placeholder="Cari desain..."
                        noOptionsText="Tidak ada desain lain untuk brand ini."
                        :errorLabel="{
                            title: 'Oops! Ada kesalahan',
                            text : 'Gagal mengambil daftar desain. Silahkan coba lagi.',
                        }"
                    >
                        <template #option="{ option, isSelected }">
                            <div class="min-w-0">
                                <div class="font-medium">{{ option.name }}</div>
                                <div class="text-xs" :class="isSelected(option) ? 'text-indigo-200' : 'text-gray-400'">
                                    {{ option.product_name || 'Semua produk' }}
                                    &middot; diupdate {{ useFormatTime(option.link_updated_at ?? undefined) }}
                                </div>
                            </div>
                        </template>
                    </PureMultiselectInfiniteScroll>

                    <div class="mt-3 flex justify-end">
                        <Button
                            size="xs"
                            type="save"
                            label="Lampirkan"
                            :disabled="!designsToAttach.length"
                            :loading="designForm.processing"
                            @click="attachDesigns"
                        />
                    </div>
                </div>
            </section>
        </div>

        <div class="flex h-fit flex-col gap-4">
            <section
                v-if="data.signature"
                class="overflow-hidden rounded-xl border"
                :class="data.signature.signed_at ? 'border-emerald-300' : 'border-amber-300'"
            >
                <div class="flex items-center justify-between border-b border-gray-900/5 bg-gray-50 px-6 py-3">
                    <div class="flex items-center gap-x-2 font-semibold text-gray-700">
                        <FontAwesomeIcon icon="fal fa-signature" fixed-width aria-hidden="true" />
                        Tanda tangan pembeli
                    </div>
                    <a :href="data.signature.url" target="_blank" rel="noopener" class="text-xs text-blue-500 underline">
                        Buka
                        <FontAwesomeIcon icon="fal fa-external-link" fixed-width aria-hidden="true" />
                    </a>
                </div>
                <div class="px-6 py-4 text-sm">
                    <template v-if="data.signature.signed_at">
                        <img
                            v-if="data.signature.image_url"
                            :src="data.signature.image_url"
                            alt="Tanda tangan pembeli"
                            class="mb-2 max-h-32 w-full rounded-lg border border-gray-200 bg-white object-contain"
                        />
                        <div class="flex justify-between">
                            <span class="text-gray-500">Ditandatangani</span>
                            <span class="font-medium text-emerald-600">{{ useFormatTime(data.signature.signed_at, { formatTime: 'hms' }) }}</span>
                        </div>
                    </template>
                    <template v-else>
                        <div class="mb-3 text-amber-600">Pembeli belum tanda tangan</div>
                        <CopyableTextarea label="Link tanda tangan" :text="data.signature.url" :rows="2" />
                    </template>
                </div>
            </section>

            <section class="overflow-hidden rounded-xl border border-gray-200">
                <div class="border-b border-gray-900/5 bg-gray-50 px-6 py-3 flex justify-between items-center">
                    <div class=" font-semibold text-gray-700">Invoice</div>
                    <a :href="data.public_url" target="_blank" rel="noopener" class="text-xs underline text-blue-500">
                        Buka Invoice
                        <FontAwesomeIcon icon='fal fa-external-link' class='' fixed-width aria-hidden='true' />
                    </a>
                </div>
                <div class="px-6 py-4 text-sm">

                    <CopyableTextarea label="Public URL" :text="data.public_url" :rows="2" class="mb-3" />

                    <div class="flex flex-col gap-3">
                        <!-- <Button full type="tertiary" icon="fal fa-link" label="Salin link invoice" @click="useCopyText(data.public_url)" /> -->
                        <CopyableTextarea label="Pesan WA" :text="invoiceMessage" :rows="3" />
                        <CopyableTextarea label="Konfirmasi order" :text="confirmationText" :rows="10" />
                    </div>
                </div>
                <dl class="divide-y divide-gray-100 px-6 pb-4 text-sm">
                    <div class="flex justify-between py-2">
                        <dt class="text-gray-500">Tgl. invoice</dt>
                        <dd class="font-medium">{{ useFormatTime(data.invoice_date) }}</dd>
                    </div>
                    <div class="flex justify-between py-2">
                        <dt class="text-gray-500">Estimasi selesai</dt>
                        <dd class="font-medium">{{ useFormatTime(data.estimated_finished_at) }}</dd>
                    </div>
                    <div class="flex justify-between py-2">
                        <dt class="text-gray-500">No. penerima</dt>
                        <dd class="text-right font-medium">
                            {{ data.recipient_phone || data.brand.recipient_phone || '-' }}
                            <span v-if="!data.recipient_phone" class="block text-xs font-normal text-gray-400">Mengikuti data brand</span>
                        </dd>
                    </div>
                    <div class="flex justify-between py-2">
                        <dt class="text-gray-500">Kasir</dt>
                        <dd class="font-medium">{{ data.sales_name }}</dd>
                    </div>
                    <div class="flex justify-between gap-x-2 py-2">
                        <dt class="text-gray-500">Jenis cetak</dt>
                        <dd class="flex flex-wrap justify-end gap-1">
                            <Tag
                                v-if="printTypeCounts.ditakurnia"
                                :label="`${printTypeCounts.ditakurnia} cetak Dita Kurnia`"
                                :theme="4"
                                noHoverColor
                            />
                            <Tag
                                v-if="printTypeCounts.luar"
                                :label="`${printTypeCounts.luar} cetak luar`"
                                :theme="8"
                                noHoverColor
                            />
                        </dd>
                    </div>
                </dl>
            </section>

            <section v-if="!data.canceled_at" class="rounded-xl border border-red-200 p-4">
                <ModalConfirmationDelete
                    :routeDelete="{ name: routes.cancel.name, parameters: routes.cancel.parameters, method: 'patch' }"
                    isFullLoading
                    title="Batalkan pesanan?"
                    description="Membatalkan pesanan, juga akan membatalkan produksi dan pesanan tidak akan ditagihkan di invoice."
                    yesLabel="Ya, batalkan pesanan"
                    yesIcon="fal fa-undo-alt"
                >
                    <template #default="{ changeModel, isLoadingdelete }">
                        <Button
                            full
                            type="delete"
                            label="Batalkan pesanan"
                            :loading="isLoadingdelete"
                            @click="() => changeModel()"
                        />
                    </template>
                </ModalConfirmationDelete>
            </section>

            <section v-if="isSuperAdmin" class="rounded-xl border border-red-200 p-4">
                <ModalConfirmationDelete
                    :routeDelete="{ name: routes.delete.name, parameters: routes.delete.parameters, method: 'delete' }"
                    isFullLoading
                    title="Hapus pesanan?"
                    description="Pesanan beserta produk, pembayaran, dan produksinya akan dihapus permanen dan tidak bisa dikembalikan."
                    yesLabel="Ya, hapus pesanan"
                    yesIcon="fal fa-trash-alt"
                >
                    <template #default="{ changeModel, isLoadingdelete }">
                        <Button
                            full
                            type="delete"
                            label="Hapus pesanan"
                            icon="fal fa-trash-alt"
                            :loading="isLoadingdelete"
                            @click="() => changeModel()"
                        />
                    </template>
                </ModalConfirmationDelete>
            </section>
        </div>
    </div>

    <AddOrderDesignModal
        :isOpen="isAddDesignOpen"
        :storeRoute="routes.store_design"
        :products="data.products"
        @onClose="isAddDesignOpen = false"
        @onCreated="onDesignCreated"
    />

    <Dialog
        :visible="!!pendingQuantityChange"
        modal
        dismissableMask
        header="Ubah jumlah pesanan?"
        :style="{ width: '28rem' }"
        :breakpoints="{ '640px': '90vw' }"
        @update:visible="visible => !visible && (pendingQuantityChange = null)"
    >
        <div v-if="pendingQuantityChange" class="text-sm text-gray-600">
            Jumlah <span class="font-semibold text-gray-800">{{ pendingQuantityChange.product.name }}</span> akan diubah dari
            <span class="font-semibold text-gray-800">{{ useFormatNumber(pendingQuantityChange.product.quantity) }} pcs</span> menjadi
            <span class="font-semibold text-gray-800">{{ useFormatNumber(pendingQuantityChange.shortcut.quantity) }} pcs</span>
            ({{ pendingQuantityChange.shortcut.label.toLowerCase() }}).
            <div class="mt-2 text-xs text-gray-500">
                Subtotal menjadi
                {{ useFormatCurrency(pendingQuantityChange.product.price * pendingQuantityChange.shortcut.quantity) }}
                dan total tagihan dihitung ulang.
            </div>
        </div>
        <template #footer>
            <Button type="tertiary" label="Batal" @click="pendingQuantityChange = null" />
            <Button type="save" label="Ya, ubah jumlah" :loading="isApplyingQuantityChange" @click="applyQuantityChange" />
        </template>
    </Dialog>
</template>
